import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/UserAddress';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const USER_ADDRESS_API_REDUCER_KEY = 'user-addresses';
export const USER_ADDRESS_PATH = 'user-addresses';

export const userAddressApi = createApi({
    reducerPath: USER_ADDRESS_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['UserAddress', 'UserAddresses'],
    endpoints: (builder) => ({
        getUserAddressList: builder.query<PaginationResult<Entity.UserAddress>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ USER_ADDRESS_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['UserAddresses'],
        }),
        getUserAddress: builder.query<Entity.UserAddress, Entity.PrimaryUserAddress>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = userAddressNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ USER_ADDRESS_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['UserAddress'],
        }),
        getUserAddressMultiple: builder.query<Entity.UserAddress, Entity.PrimaryUserAddress[]>({
            query: (ids) => {
                return ({
                    url: `/${ USER_ADDRESS_PATH }/ids${Entity.PrimaryUserAddress.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['UserAddresses'],
        }),
        getUserAddressPresentation: builder.query<PaginationResult<Entity.UserAddress>, Entity.PresentationUserAddressRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ USER_ADDRESS_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['UserAddresses'],
        }),
        
        insertUserAddress: builder.mutation<Entity.UserAddress, Entity.CreateUserAddress>({
            query: (toInsert) => {
                return ({
                    url: `/${ USER_ADDRESS_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    userAddressApi.util.upsertQueryData('getUserAddress', Entity.PrimaryUserAddress.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['UserAddresses'],
        }),
        insertMultipleUserAddress: builder.mutation<Entity.UserAddress[], Entity.CreateUserAddress[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ USER_ADDRESS_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            userAddressApi.util.upsertQueryData('getUserAddress', Entity.PrimaryUserAddress.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['UserAddresses'],
        }),
        
        deleteUserAddress: builder.mutation<Entity.UserAddress, Entity.UserAddress>({
            query: (toDelete) => {
                return ({
                    url: `/${ USER_ADDRESS_PATH }/${Entity.PrimaryUserAddress.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['UserAddresses'],
        }),
        deleteMultipleUserAddress: builder.mutation<Entity.UserAddress[], Entity.PrimaryUserAddress[]>({
            query: (ids) => {
                return ({
                    url: `/${ USER_ADDRESS_PATH }/batch${Entity.PrimaryUserAddress.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['UserAddresses'],
        }),
    }),
})

export const [userAddressNormalizer, userAddressNormalizerMiddleware] = createNormalizationMiddleware(USER_ADDRESS_API_REDUCER_KEY, userAddressApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetUserAddressListQuery,
    useGetUserAddressMultipleQuery,
    useGetUserAddressPresentationQuery,
    useGetUserAddressQuery,
    useInsertUserAddressMutation,
    useInsertMultipleUserAddressMutation,
    
    useDeleteUserAddressMutation,
    useDeleteMultipleUserAddressMutation,
    
} = userAddressApi
